import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Filter, X, Download, ChevronsUpDown } from 'lucide-react';
import { Solicitacao } from '@/types/solicitacao';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';

export interface RequisicoesFiltersState {
  grupo: string;
  cliente: string;
  status: string[];
  solicitante: string;
  sla: string;
  atendente: string;
  numero: string;
}

interface RequisicoesFiltersProps {
  filters: RequisicoesFiltersState;
  onFiltersChange: (filters: RequisicoesFiltersState) => void;
  onClearFilters: () => void;
  onExportCSV: () => void;
  requisicoes: Solicitacao[];
  isVisible: boolean;
  onToggleVisibility: () => void;
}

export const RequisicoesFilters: React.FC<RequisicoesFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
  onExportCSV,
  requisicoes,
  isVisible,
  onToggleVisibility,
}) => {
  // Extrair valores únicos para os selects com verificação de segurança
  const uniqueGrupos = React.useMemo(() => {
    if (!requisicoes || requisicoes.length === 0) return [];
    return [...new Set(requisicoes.map(r => r.grupo_responsavel?.name).filter(Boolean))];
  }, [requisicoes]);
  
  const uniqueClientes = React.useMemo(() => {
    if (!requisicoes || requisicoes.length === 0) return [];
    return [...new Set(requisicoes.map(r => r.cliente?.name).filter(Boolean))];
  }, [requisicoes]);
  
  const uniqueStatus = React.useMemo(() => {
    if (!requisicoes || requisicoes.length === 0) return [];
    return [...new Set(requisicoes.map(r => r.status).filter(Boolean))];
  }, [requisicoes]);
  
  const uniqueSolicitantes = React.useMemo(() => {
    if (!requisicoes || requisicoes.length === 0) return [];
    return [...new Set(requisicoes.map(r => r.solicitante?.name).filter(Boolean))];
  }, [requisicoes]);
  
  const uniqueAtendentes = React.useMemo(() => {
    if (!requisicoes || requisicoes.length === 0) return [];
    return [...new Set(requisicoes.map(r => r.atendente?.name).filter(Boolean))];
  }, [requisicoes]);

  const handleFilterChange = (key: keyof RequisicoesFiltersState, value: string) => {
    // Converter valores especiais para string vazia para compatibilidade com a lógica de filtragem
    const actualValue = value === '__all__' || value.startsWith('__empty__') ? '' : value;
    onFiltersChange({
      ...filters,
      [key]: actualValue,
    });
  };

  const handleStatusToggle = (status: string) => {
    const current = filters.status;
    const updated = current.includes(status)
      ? current.filter(s => s !== status)
      : [...current, status];
    onFiltersChange({ ...filters, status: updated });
  };

  const hasActiveFilters = Object.entries(filters).some(([key, value]) =>
    key === 'status' ? (value as string[]).length > 0 : value !== ''
  );

  return (
    <div className="space-y-4">
      {/* Header com botões de controle */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={onToggleVisibility}
          className="flex items-center gap-2"
        >
          <Filter className="h-4 w-4" />
          {isVisible ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          {hasActiveFilters && (
            <span className="ml-1 bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs">
              {Object.entries(filters).filter(([k, v]) => k === 'status' ? (v as string[]).length > 0 : v !== '').length}
            </span>
          )}
        </Button>

        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Limpar Filtros
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onExportCSV}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Painel de filtros */}
      {isVisible && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* Filtro por Número */}
              <div className="space-y-2">
                <Label htmlFor="numero-filter">Número</Label>
                <Input
                  id="numero-filter"
                  placeholder="Ex: REQ-001"
                  value={filters.numero}
                  onChange={(e) => handleFilterChange('numero', e.target.value)}
                />
              </div>

              {/* Filtro por Grupo */}
              <div className="space-y-2">
                <Label htmlFor="grupo-filter">Grupo</Label>
                <Select
                  value={filters.grupo || '__all__'}
                  onValueChange={(value) => handleFilterChange('grupo', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os grupos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os grupos</SelectItem>
                    {uniqueGrupos.map((grupo, index) => (
                      <SelectItem key={`grupo-${index}-${grupo}`} value={grupo || `__empty__${index}`}>
                        {grupo || 'Sem grupo'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro por Cliente */}
              <div className="space-y-2">
                <Label htmlFor="cliente-filter">Cliente</Label>
                <Select
                  value={filters.cliente || '__all__'}
                  onValueChange={(value) => handleFilterChange('cliente', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os clientes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os clientes</SelectItem>
                    {uniqueClientes.map((cliente, index) => (
                      <SelectItem key={`cliente-${index}-${cliente}`} value={cliente || `__empty__${index}`}>
                        {cliente || 'Sem cliente'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro por Status (Multi-Select) */}
              <div className="space-y-2">
                <Label>Status</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between font-normal">
                      {filters.status.length === 0
                        ? 'Todos os status'
                        : `${filters.status.length} selecionado(s)`}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-2 bg-popover" align="start">
                    <div className="space-y-1">
                      {uniqueStatus.map((status, index) => (
                        <label
                          key={`status-${index}-${status}`}
                          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer hover:bg-accent"
                        >
                          <Checkbox
                            checked={filters.status.includes(status)}
                            onCheckedChange={() => handleStatusToggle(status)}
                          />
                          {status ? status.replace('_', ' ') : 'Sem status'}
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Filtro por Solicitante */}
              <div className="space-y-2">
                <Label htmlFor="solicitante-filter">Solicitante</Label>
                <Select
                  value={filters.solicitante || '__all__'}
                  onValueChange={(value) => handleFilterChange('solicitante', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os solicitantes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os solicitantes</SelectItem>
                    {uniqueSolicitantes.map((solicitante, index) => (
                      <SelectItem key={`solicitante-${index}-${solicitante}`} value={solicitante || `__empty__${index}`}>
                        {solicitante || 'Sem solicitante'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro por Atendente */}
              <div className="space-y-2">
                <Label htmlFor="atendente-filter">Atendente</Label>
                <Select
                  value={filters.atendente || '__all__'}
                  onValueChange={(value) => handleFilterChange('atendente', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os atendentes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os atendentes</SelectItem>
                    {uniqueAtendentes.map((atendente, index) => (
                      <SelectItem key={`atendente-${index}-${atendente}`} value={atendente || `__empty__${index}`}>
                        {atendente || 'Sem atendente'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
